import { Alert, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { FC, useState } from 'react';

import { DefaultVehicleOfferFragment, VehiclePaymentTiming } from '../../../graphql/generated/graphql';
import { formatCurrency } from '../../../helpers/formatCurrency';
import { Iconify } from '../../iconify';

/** A car as the counter payment reads it */
export type CounterPaymentVehicle = Pick<DefaultVehicleOfferFragment, 'id' | 'vendor' | 'price'> & {
  paymentTiming?: VehiclePaymentTiming | null;
};

/** Who collects the amount and that we never charge it, as the hotel's "Due at property" says it for a property fee */
export function collectedByVendor(vendorName: string): string {
  return `This amount is collected by ${vendorName} when you pick up the car. Altitude AI does not charge it.`;
}

/** One car paid at the desk, as the hotel's property fee: what is due and to whom in bold, the amount plain */
const CounterPaymentAlert: FC<{ vehicle: CounterPaymentVehicle }> = ({ vehicle }) => {
  const [explained, setExplained] = useState(false);
  const { price, vendor } = vehicle;
  const vendorTotal = price.vendorTotal;

  return (
    <Alert
      severity="info"
      icon={false}
      sx={{ '& .MuiAlert-message': { width: '100%' } }}
      data-test="vehicle-counter-payment"
    >
      <Stack spacing={1}>
        <Stack direction="row" sx={{ justifyContent: 'space-between', gap: 1 }}>
          <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
            <Typography
              variant="body2"
              sx={{ fontSize: '0.75rem', fontWeight: 700 }}
              data-test="vehicle-counter-payment-title"
            >
              Due at pick up at {vendor.name}
            </Typography>
            <Tooltip
              open={explained || undefined}
              onClose={() => setExplained(false)}
              title={<Typography variant="caption">{collectedByVendor(vendor.name)}</Typography>}
            >
              <IconButton
                size="small"
                onClick={() => setExplained((open) => !open)}
                aria-label="What is due at pick up"
                sx={{ p: 0.25 }}
              >
                <Iconify icon="ph:info" width={14} />
              </IconButton>
            </Tooltip>
          </Stack>
          <Stack spacing={0.25} sx={{ alignItems: 'flex-end' }}>
            <Typography
              variant="body2"
              sx={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}
              data-test="vehicle-counter-payment-amount"
            >
              {vendorTotal
                ? `${vendorTotal.currency} ${formatCurrency(vendorTotal.amount, vendorTotal.currency, vendorTotal.currency)}`
                : `${price.currency} ${formatCurrency(price.total, price.currency, price.currency)}`}
            </Typography>
            {vendorTotal && (
              <Typography variant="body2" sx={{ fontSize: '0.7rem' }}>
                (~{price.currency} {formatCurrency(price.total, price.currency, price.currency)}*)
              </Typography>
            )}
          </Stack>
        </Stack>
        {vendorTotal && (
          <Typography variant="caption" sx={{ fontSize: '0.6rem' }}>
            *This conversion is an approximate price based on the current exchange rate and may change before your pick
            up.
          </Typography>
        )}
      </Stack>
    </Alert>
  );
};

/**
 * The cars paid at the counter, set out like the hotel's "Due at property" fee: what the vendor collects when the car
 * is picked up. The trip total counts it (the Car row), but Altitude AI does not charge it, so this says who does.
 * Prepaid cars are not shown here.
 */
export const VehicleCounterPayments: FC<{ vehicles: CounterPaymentVehicle[] }> = ({ vehicles }) => {
  const counterCars = vehicles.filter((vehicle) => vehicle.paymentTiming !== VehiclePaymentTiming.Prepaid);

  if (counterCars.length === 0) {
    return null;
  }

  return (
    <Stack spacing={1}>
      {counterCars.map((vehicle) => (
        <CounterPaymentAlert key={vehicle.id} vehicle={vehicle} />
      ))}
    </Stack>
  );
};
